import React, { useState } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  Login as LoginIcon,
  Logout as LogoutIcon,
  AccountCircle,
  Dashboard as DashboardIcon,
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
  Save as SaveIcon
} from '@mui/icons-material';

import MobileNotifications from './components/MobileNotifications';
import NotificationsWidget from './components/NotificationsWidget';
import MobileWorkOrders from './components/MobileWorkOrders';
import WorkOrdersWidget from './components/WorkOrdersWidget';
import MobileQRScanner from './components/MobileQRScanner';
import QRScannerWidget from './components/QRScannerWidget';
import MobileProductionPlan from './components/MobileProductionPlan';
import MobileQuickScan from './components/MobileQuickScan';
import QuickScanWidget from './components/QuickScanWidget';
import MobileMaterialCertificate from './components/MobileMaterialCertificate';
import MaterialCertificateWidget from './components/MaterialCertificateWidget';
import MobileContacts from './components/MobileContacts';
import ContactsWidget from './components/ContactsWidget';
import MobileFeed from './components/MobileFeed';
import FeedWidgetMobile from './components/FeedWidgetMobile';
import MobileMyJobs from './components/MobileMyJobs';
import MyJobsWidget from './components/MyJobsWidget';
import DemoModeBanner from './components/DemoModeBanner';
import NetworkStatusBanner from './components/NetworkStatusBanner';
import { demoUser } from './services/demoData';
import { DEMO_MODE } from './services/demoData';
import networkMonitor from './services/networkMonitor';
// import pushNotificationService from './services/pushNotificationService'; // DISABLED temporarily

// Helper to get API URL from localStorage
const getApiUrl = () => {
  return localStorage.getItem('apiUrl') || '';
};

const MobileApp = () => {
  // API URL setup state
  const [apiUrl, setApiUrl] = useState(getApiUrl());
  const [apiUrlInput, setApiUrlInput] = useState(getApiUrl());
  const [showApiSetup, setShowApiSetup] = useState(!getApiUrl());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'notifications', 'workorders', 'qrscanner', 'productionplan', 'quickscan', 'certificates', 'feed', 'myjobs'
  const [sessionExpiredAlert, setSessionExpiredAlert] = useState(false);
  // const [pushToken, setPushToken] = useState(null); // DISABLED - not used currently
  const [userPermissions, setUserPermissions] = useState({
    canCreateWorkOrder: false,
    canCreateCertificate: false,
    employeeId: null
  });
  const [refreshKey, setRefreshKey] = useState(0); // For triggering widget refreshes

  // Save API URL
  const handleSaveApiUrl = () => {
    if (apiUrlInput.trim()) {
      // Remove trailing slash if present
      const cleanUrl = apiUrlInput.trim().replace(/\/+$/, '');
      localStorage.setItem('apiUrl', cleanUrl);
      setApiUrl(cleanUrl);
      setShowApiSetup(false);
      setError('');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Check for demo credentials
    if (email === 'demo@demo.no' && password === 'demo') {
      handleDemoLogin();
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.token) {
        // Clear demo mode when doing normal login
        localStorage.removeItem('demoMode');
        DEMO_MODE.enabled = false;
        
        // Save token and user info
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        setUser(data.user);
        setIsAuthenticated(true);
        
        // Fetch user permissions
        try {
          console.log('[MobileApp] Fetching permissions for user:', data.user.email);
          const permResponse = await fetch(`${apiUrl}/user/permissions`, {
            headers: {
              'Authorization': `Bearer ${data.token}`
            }
          });
          
          console.log('[MobileApp] Permissions response status:', permResponse.status);
          
          if (permResponse.ok) {
            const permissions = await permResponse.json();
            console.log('[MobileApp] Permissions received:', permissions);
            setUserPermissions(permissions);
            localStorage.setItem('userPermissions', JSON.stringify(permissions));
          } else {
            console.error('[MobileApp] Permissions request failed:', permResponse.status);
          }
        } catch (permError) {
          console.error('[MobileApp] Failed to fetch permissions:', permError);
          // Continue anyway - user can still use app with default permissions
        }
        
        // Initialize push notifications (DISABLED temporarily to fix login)
        // TODO: Re-enable after Firebase is configured
        /*
        try {
          initializePushNotifications(data.user.id, data.token).catch(err => {
            console.log('[MobileApp] Push notification init failed (non-critical):', err);
          });
        } catch (err) {
          console.log('[MobileApp] Push notification setup failed (non-critical):', err);
        }
        */
      } else {
        setError(data.error || 'Innlogging feilet');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Kunne ikke koble til server. Sjekk nettverkstilkoblingen.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    // Unregister push notifications (DISABLED temporarily)
    /*
    try {
      await pushNotificationService.unregister();
    } catch (err) {
      console.log('[MobileApp] Push notification unregister failed (non-critical):', err);
    }
    */
    
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userPermissions');
    localStorage.removeItem('demoMode');
    DEMO_MODE.enabled = false;
    setIsAuthenticated(false);
    setUser(null);
    setEmail('');
    setPassword('');
    setSessionExpiredAlert(false);
    // setPushToken(null); // DISABLED
    setUserPermissions({
      canCreateWorkOrder: false,
      canCreateCertificate: false,
      employeeId: null
    });
  };

  // Demo login for App Store reviewers
  const handleDemoLogin = () => {
    DEMO_MODE.enabled = true;
    DEMO_MODE.timestamp = new Date().toISOString();
    
    const demoToken = 'demo-token-' + Date.now();
    const demoUserData = {
      id: demoUser.id,
      email: demoUser.email,
      firstName: 'Demo',
      lastName: 'Bruker',
      isAdmin: true,
    };
    
    const demoPermissions = {
      canCreateWorkOrder: true,
      canCreateCertificate: true,
      employeeId: 1,
      isAdmin: true,
    };
    
    localStorage.setItem('token', demoToken);
    localStorage.setItem('user', JSON.stringify(demoUserData));
    localStorage.setItem('demoMode', 'true');
    localStorage.setItem('userPermissions', JSON.stringify(demoPermissions));
    
    setUser(demoUserData);
    setIsAuthenticated(true);
    setUserPermissions(demoPermissions);
  };

  const handleSessionExpired = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userPermissions');
    setIsAuthenticated(false);
    setUser(null);
    setSessionExpiredAlert(true);
    setCurrentView('dashboard');
    setUserPermissions({
      canCreateWorkOrder: false,
      canCreateCertificate: false,
      employeeId: null
    });
  };

  // Initialize push notifications (DISABLED temporarily)
  /*
  const initializePushNotifications = async (userId, token) => {
    try {
      console.log('[MobileApp] Initializing push notifications for user:', userId);
      const deviceToken = await pushNotificationService.initialize(userId);
      
      if (deviceToken) {
        setPushToken(deviceToken);
        
        // Register device token with backend
        try {
          await fetch(`${getApiUrl()}/push-notifications/register`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              deviceToken,
              platform: 'android',
              userId
            })
          });
          console.log('[MobileApp] Device token registered with backend');
        } catch (registerError) {
          console.error('[MobileApp] Failed to register device token:', registerError);
        }
      }
    } catch (error) {
      console.error('[MobileApp] Push notification initialization error:', error);
    }
  };

  // Handle push notification received (while app is open)
  React.useEffect(() => {
    const handlePushReceived = (event) => {
      console.log('[MobileApp] Push notification received:', event.detail);
      
      // Show notification badge or update UI
      // You can add a toast notification here
      const notification = event.detail;
      
      // Optionally navigate to notifications view
      if (notification?.data?.type === 'job_ready') {
        // Show alert or navigate
        console.log('[MobileApp] Job ready notification received');
      }
    };

    const handlePushAction = (event) => {
      console.log('[MobileApp] Push notification action performed:', event.detail);
      
      // Navigate to notifications when user taps notification
      setCurrentView('notifications');
    };

    window.addEventListener('pushNotificationReceived', handlePushReceived);
    window.addEventListener('pushNotificationActionPerformed', handlePushAction);

    return () => {
      window.removeEventListener('pushNotificationReceived', handlePushReceived);
      window.removeEventListener('pushNotificationActionPerformed', handlePushAction);
    };
  }, []);
  */

  // Initialize push notifications on app start if already logged in (DISABLED)
  /*
  React.useEffect(() => {
    if (isAuthenticated && user) {
      const token = localStorage.getItem('token');
      if (token && !pushToken) {
        try {
          initializePushNotifications(user.id, token).catch(err => {
            console.log('[MobileApp] Push notification init failed (non-critical):', err);
          });
        } catch (err) {
          console.log('[MobileApp] Push notification setup failed (non-critical):', err);
        }
      }
    }
  }, [isAuthenticated, user, pushToken]);
  */

  // Global error handler for 401 errors
  React.useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      
      // Check if response is 401 Unauthorized
      if (response.status === 401 && isAuthenticated) {
        console.log('Token expired - logging out');
        handleSessionExpired();
      }
      
      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [isAuthenticated]);

  // Check if already logged in
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedPermissions = localStorage.getItem('userPermissions');
    
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        setIsAuthenticated(true);
        
        // Restore permissions if available
        if (savedPermissions) {
          try {
            setUserPermissions(JSON.parse(savedPermissions));
          } catch (permErr) {
            console.error('Error parsing saved permissions:', permErr);
          }
        }
      } catch (err) {
        console.error('Error parsing saved user:', err);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userPermissions');
      }
    }
  }, []);

  // Network reconnect handler - refresh widgets when network comes back
  React.useEffect(() => {
    if (!isAuthenticated) return;

    const handleReconnect = () => {
      console.log('[MobileApp] Network reconnected - refreshing data');
      // Trigger widget refresh by incrementing key
      setRefreshKey(prev => prev + 1);
    };

    networkMonitor.onReconnect(handleReconnect);

    // Note: No cleanup needed as networkMonitor is a singleton
    // and keeps callbacks for the lifetime of the app
  }, [isAuthenticated]);

  // API URL Setup Screen (first time)
  if (showApiSetup) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2
        }}
      >
        <Container maxWidth="sm">
          <Card elevation={8} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    margin: '0 auto 16px',
                    bgcolor: 'primary.main'
                  }}
                >
                  <SettingsIcon sx={{ fontSize: 50 }} />
                </Avatar>
                <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary">
                  Oppsett
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Skriv inn serveradresse for å komme i gang
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Server URL"
                placeholder="http://din-server:4000"
                value={apiUrlInput}
                onChange={(e) => setApiUrlInput(e.target.value)}
                margin="normal"
                variant="outlined"
                helperText="Eksempel: http://din-server:4000 eller https://din-server.no"
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSaveApiUrl}
                disabled={!apiUrlInput.trim()}
                startIcon={<SaveIcon />}
                sx={{
                  mt: 3,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  textTransform: 'none'
                }}
              >
                Lagre og fortsett
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2
        }}
      >
        <Container maxWidth="sm">
          <Card elevation={8} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    margin: '0 auto 16px',
                    bgcolor: 'primary.main'
                  }}
                >
                  <AccountCircle sx={{ fontSize: 60 }} />
                </Avatar>
                <Typography variant="h5" component="h1" gutterBottom fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }}>
                  Produksjonsstyring
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Logg inn for å fortsette
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleLogin} sx={{ mt: 2 }}>
                {sessionExpiredAlert && (
                  <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSessionExpiredAlert(false)}>
                    Din sesjon har utløpt. Vennligst logg inn på nytt.
                  </Alert>
                )}
                
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="E-post"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  margin="normal"
                  variant="outlined"
                  autoComplete="email"
                />

                <TextField
                  fullWidth
                  label="Passord"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  margin="normal"
                  variant="outlined"
                  autoComplete="current-password"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    textTransform: 'none'
                  }}
                >
                  {loading ? 'Logger inn...' : 'Logg inn'}
                </Button>

                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowApiSetup(true)}
                  startIcon={<SettingsIcon />}
                  sx={{ mt: 2, textTransform: 'none' }}
                >
                  Server: {apiUrl || 'Ikke satt'}
                </Button>

              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  // Main Page (Logged In)
  const userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : 'Bruker';

  // Check if user is "guest" waiting for approval
  const isGuest = user && !user.isAdmin && !userPermissions.employeeId;
  
  // Debug logging
  console.log('[MobileApp] User:', user?.email, 'isAdmin:', user?.isAdmin);
  console.log('[MobileApp] Permissions:', userPermissions);
  console.log('[MobileApp] isGuest:', isGuest);

  // Guest state - waiting for approval
  if (isGuest) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f5f5f5' }}>
        {/* Header */}
        <AppBar position="static" elevation={2}>
          <Toolbar>
            <DashboardIcon sx={{ mr: 2 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" component="div" fontWeight="bold">
                Produksjonsstyring
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Hei, {userName}
              </Typography>
            </Box>
            <IconButton
              color="inherit"
              onClick={handleLogout}
              edge="end"
            >
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Pending approval content */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 3,
          textAlign: 'center'
        }}>
          <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>
            ⏳
          </Typography>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Venter på godkjenning
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '500px', mb: 1 }}>
            Din bruker er opprettet, men har ingen tilgang ennå.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '500px' }}>
            Vennligst vent til en administrator har tildelt deg en rolle og rettigheter.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Dashboard visning
  const renderDashboard = () => (
    <Box sx={{ mt: 2, px: { xs: 1, sm: 2 }, overflow: 'hidden', maxWidth: '100vw' }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, px: { xs: 1, sm: 0 } }}>
        Dashboard
      </Typography>
      
      {/* Rad 1: Mine Jobber */}
      <Box sx={{ mb: 1, width: '100%' }}>
        <MyJobsWidget onClick={() => setCurrentView('myjobs')} />
      </Box>
      
      {/* Rad 2: Ordre håndtering og Produksjonslager */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1, width: '100%' }}>
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
          <QRScannerWidget onClick={() => setCurrentView('qrscanner')} />
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
          <QuickScanWidget onClick={() => setCurrentView('quickscan')} />
        </Box>
      </Box>
      
      {/* Rad 3: Kontakter og Material Sertifikater */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1, width: '100%' }}>
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
          <ContactsWidget onClick={() => setCurrentView('contacts')} />
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
          <MaterialCertificateWidget onClick={() => setCurrentView('certificates')} />
        </Box>
      </Box>
      
      {/* Rad 4: Internfeed og Varslinger */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1, width: '100%' }}>
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
          <FeedWidgetMobile onClick={() => setCurrentView('feed')} />
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
          <NotificationsWidget key={refreshKey} onClick={() => setCurrentView('notifications')} />
        </Box>
      </Box>
      
      {/* Rad 5: Arbeidsordrer */}
      <Box sx={{ mb: 1, width: '100%' }}>
        <WorkOrdersWidget key={refreshKey} onClick={() => setCurrentView('workorders')} currentUser={user} />
      </Box>
    </Box>
  );

  // Full notifications visning
  const renderNotifications = () => (
    <Box sx={{ mt: 2, px: { xs: 1, sm: 2 }, width: '100%' }}>
      <MobileNotifications key={refreshKey} />
    </Box>
  );

  // Full work orders visning
  const renderWorkOrders = () => (
    <Box sx={{ mt: 2, px: { xs: 1, sm: 2 }, width: '100%' }}>
      <MobileWorkOrders 
        key={refreshKey} 
        onNavigateToQRScanner={(orderId, qrData) => {
          // Lagre ordre-info for QR scanner å laste
          localStorage.setItem('preloadOrderId', orderId);
          localStorage.setItem('preloadQRData', qrData);
          setCurrentView('qrscanner');
        }}
      />
    </Box>
  );

  // QR Scanner visning
  const renderQRScanner = () => (
    <Box sx={{ mt: 2, px: { xs: 1, sm: 2 }, width: '100%' }}>
      <MobileQRScanner />
    </Box>
  );

  // Production Plan visning
  const renderProductionPlan = () => (
    <Box sx={{ mt: 2, px: { xs: 1, sm: 2 }, width: '100%' }}>
      <MobileProductionPlan key={refreshKey} />
    </Box>
  );

  // Quick Scan visning
  const renderQuickScan = () => (
    <Box sx={{ mt: 2, px: { xs: 1, sm: 2 }, width: '100%' }}>
      <MobileQuickScan />
    </Box>
  );

  // Material Certificates visning
  const renderCertificates = () => (
    <Box sx={{ mt: 2, px: { xs: 1, sm: 2 }, width: '100%' }}>
      <MobileMaterialCertificate key={refreshKey} />
    </Box>
  );

  // Full feed visning
  const renderFeed = () => (
    <Box sx={{ mt: 2, px: { xs: 1, sm: 2 }, width: '100%' }}>
      <MobileFeed key={refreshKey} />
    </Box>
  );

  // Contacts visning
  const renderContacts = () => (
    <Box sx={{ mt: 2, px: { xs: 1, sm: 2 }, width: '100%' }}>
      <MobileContacts />
    </Box>
  );

  // My Jobs visning
  const renderMyJobs = () => (
    <Box sx={{ mt: 2, px: { xs: 1, sm: 2 }, width: '100%' }}>
      <MobileMyJobs key={refreshKey} />
    </Box>
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh', 
      bgcolor: '#f5f5f5', 
      overflow: 'hidden',
      paddingTop: 'env(safe-area-inset-top)',
      paddingLeft: 'env(safe-area-inset-left)',
      paddingRight: 'env(safe-area-inset-right)',
      paddingBottom: 'env(safe-area-inset-bottom)'
    }}>
      <DemoModeBanner />
      <NetworkStatusBanner />
      {/* Header */}
      <AppBar position="static" elevation={2} sx={{ flexShrink: 0 }}>
        <Toolbar>
          {currentView !== 'dashboard' && (
            <IconButton
              color="inherit"
              onClick={() => setCurrentView('dashboard')}
              edge="start"
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          {currentView === 'dashboard' && <DashboardIcon sx={{ mr: 2 }} />}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div" fontWeight="bold">
              {currentView === 'dashboard' ? 'Produksjonsstyring' : 
               currentView === 'notifications' ? 'Varsler' : 
               currentView === 'workorders' ? 'Arbeidsordrer' : 
               currentView === 'qrscanner' ? 'Ordre håndtering' : 
               currentView === 'quickscan' ? 'Produksjonslager' : 
               currentView === 'contacts' ? 'Kontakter' :
               currentView === 'feed' ? 'Internfeed' :
               currentView === 'myjobs' ? 'Mine Jobber' :
               currentView === 'certificates' ? 'Sertifikater' : 'Produksjonsplan'}
            </Typography>
            {currentView === 'dashboard' && (
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Hei, {userName}
              </Typography>
            )}
          </Box>
          <IconButton
            color="inherit"
            onClick={handleLogout}
            edge="end"
          >
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        overflowY: 'scroll',
        WebkitOverflowScrolling: 'touch',
        pb: 4 
      }}>
        {currentView === 'dashboard' && renderDashboard()}
        {currentView === 'notifications' && renderNotifications()}
        {currentView === 'workorders' && renderWorkOrders()}
        {currentView === 'qrscanner' && renderQRScanner()}
        {currentView === 'productionplan' && renderProductionPlan()}
        {currentView === 'quickscan' && renderQuickScan()}
        {currentView === 'feed' && renderFeed()}
        {currentView === 'contacts' && renderContacts()}
        {currentView === 'myjobs' && renderMyJobs()}
        {currentView === 'certificates' && renderCertificates()}
      </Box>
    </Box>
  );
};

export default MobileApp;
